import { supabase } from './supabase';

export interface TrelloCard {
  id: string;
  name: string;
  desc: string;
  idList: string;
  due?: string;
  labels?: string[];
  idMembers?: string[];
  url: string;
}

export interface TrelloBoard {
  id: string;
  name: string;
  url: string;
}

export interface TrelloList {
  id: string;
  name: string;
  idBoard: string;
}

export interface TrelloMember {
  id: string;
  fullName: string;
  username: string;
  avatarUrl?: string;
  // No email storage for privacy
}

export interface TrelloLabel {
  id: string;
  name: string;
  color: string;
  idBoard: string;
}

class TrelloService {
  private token: string | null = null;
  private readonly MEMBER_CACHE_KEY = 'trello_members_cache';
  private readonly MEMBER_CACHE_DURATION = 2 * 60 * 60 * 1000; // 2 hours (reduced from 24h for just-in-time approach)

  // Call this after user login to fetch token from DB
  async init() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user?.id) {
      this.token = null;
      return;
    }
    
    const { data, error } = await supabase
      .from('user_trello_tokens')
      .select('access_token')
      .eq('user_id', user.id)
      .single();
    
    if (error || !data) {
      this.token = null;
    } else {
      this.token = data.access_token;
    }
  }

  setToken(token: string) {
    this.token = token;
  }

  clearToken() {
    this.token = null;
  }

  // Clear all user data when disconnecting
  async clearAllUserData() {
    this.clearToken();
    this.clearMemberCache();
  }

  isConnected(): boolean {
    return !!this.token;
  }

  // Helper to call the Edge Function with automatic token refresh handling
  private async callMcp(action: string, args: Record<string, any> = {}): Promise<any> {
    if (!this.token) {
      throw new Error('Trello not connected. Please connect your account first.');
    }

    try {
      const { data, error } = await supabase.functions.invoke('trello-api', {
        body: {
          action,
          arguments: { ...args, token: this.token },
        },
      });

      if (error || data?.error) {
        const errorMessage = data?.error || error?.message || 'Trello API error';
        if (errorMessage.includes('401') || errorMessage.includes('invalid token') || errorMessage.includes('unauthorized')) {
          await this.clearInvalidToken();
          throw new Error('TRELLO_TOKEN_EXPIRED');
        }
        throw new Error(errorMessage);
      }

      return data;
    } catch (error) {
      if (error instanceof Error && error.message === 'TRELLO_TOKEN_EXPIRED') {
        throw error;
      }
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      if (errorMessage.includes('401') || errorMessage.includes('invalid token') || errorMessage.includes('unauthorized')) {
        await this.clearInvalidToken();
        throw new Error('TRELLO_TOKEN_EXPIRED');
      }
      throw error;
    }
  }

  // Clear invalid token from database
  private async clearInvalidToken() {
    const { data: { user } } = await supabase.auth.getUser();
    if (user?.id) {
      await supabase
        .from('user_trello_tokens')
        .delete()
        .eq('user_id', user.id);
    }
    this.token = null;
  }

  // Get boards
  async getBoards(): Promise<TrelloBoard[]> {
    const data = await this.callMcp('get_boards');
    return data.boards || data;
  }

  // Get lists in a board
  async getBoardLists(boardId: string): Promise<TrelloList[]> {
    const data = await this.callMcp('get_board_lists', { boardId });
    return data.lists || data;
  }

  // Get members in a board
  async getBoardMembers(boardId: string): Promise<TrelloMember[]> {
    const data = await this.callMcp('get_board_members', { boardId });
    return data.members || data;
  }

  // Get labels in a board
  async getBoardLabels(boardId: string): Promise<TrelloLabel[]> {
    const data = await this.callMcp('get_board_labels', { boardId });
    return data.labels || data;
  }

  // Create a new card
  async createCard(cardData: {
    name: string;
    desc: string;
    idList: string;
    due?: string;
    idLabels?: string[];
    idMembers?: string[];
  }): Promise<TrelloCard> {
    const data = await this.callMcp('add_card_to_list', {
      listId: cardData.idList,
      name: cardData.name,
      description: cardData.desc,
      dueDate: cardData.due,
      labels: cardData.idLabels,
      members: cardData.idMembers,
    });
    return data.card || data;
  }

  // Create multiple cards
  async createCards(cards: Array<{
    name: string;
    desc: string;
    idList: string;
    due?: string;
    idLabels?: string[];
    idMembers?: string[];
  }>): Promise<TrelloCard[]> {
    const results = await Promise.allSettled(
      cards.map(card => this.createCard(card))
    );
    const createdCards: TrelloCard[] = [];
    const errors: string[] = [];
    results.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        createdCards.push(result.value);
      } else {
        errors.push(`Card "${cards[index].name}": ${result.reason}`);
      }
    });
    if (errors.length > 0) {
      console.warn('Some cards failed to create:', errors);
    }
    return createdCards;
  }

  // Get user info (from Trello)
  async getUserInfo(): Promise<{
    id: string;
    fullName: string;
    username: string;
    email: string;
  }> {
    const data = await this.callMcp('get_my_info');
    return data.user || data;
  }

  // Get all members (client-side cached, no emails)
  async getMembers(): Promise<TrelloMember[]> {
    // Check local cache first
    const cached = this.getCachedMembers();
    if (cached) {
      console.log('📋 Using cached members');
      return cached;
    }

    // Fetch fresh data
    console.log('🔄 Fetching fresh members...');
    const response = await this.callMcp('get_members', {});
    const members = response.members || response;

    // Cache the results (without emails for privacy)
    this.cacheMembers(members.map((member: any) => ({
      id: member.id,
      fullName: member.fullName,
      username: member.username,
      avatarUrl: member.avatarUrl,
      // Don't cache email addresses
    })));

    return members;
  }

  // Get members for a specific board (client-side cached)
  async getBoardMembersForSelection(boardId: string): Promise<TrelloMember[]> {
    // Check local cache first
    const cached = this.getCachedMembers();
    if (cached) {
      console.log('📋 Using cached members for board selection');
      return cached;
    }

    // Fetch fresh data for this board
    console.log('🔄 Fetching board members...');
    const members = await this.getBoardMembers(boardId);

    // Cache the results (without emails for privacy)
    this.cacheMembers(members.map((member: TrelloMember) => ({
      id: member.id,
      fullName: member.fullName,
      username: member.username,
      avatarUrl: member.avatarUrl,
      // Don't cache email addresses
    })));

    return members;
  }

  // Client-side caching methods
  private cacheMembers(members: TrelloMember[]): void {
    try {
      const cacheData = {
        members,
        timestamp: Date.now()
      };
      localStorage.setItem(this.MEMBER_CACHE_KEY, JSON.stringify(cacheData));
      console.log('💾 Cached members locally');
    } catch (error) {
      console.warn('Failed to cache members:', error);
    }
  }

  private getCachedMembers(): TrelloMember[] | null {
    try {
      const cached = localStorage.getItem(this.MEMBER_CACHE_KEY);
      if (!cached) return null;

      const cacheData = JSON.parse(cached);
      const isExpired = Date.now() - cacheData.timestamp > this.MEMBER_CACHE_DURATION;

      if (isExpired) {
        localStorage.removeItem(this.MEMBER_CACHE_KEY);
        return null;
      }

      return cacheData.members;
    } catch (error) {
      console.warn('Failed to read cached members:', error);
      return null;
    }
  }

  // Clear local member cache
  clearMemberCache(): void {
    try {
      localStorage.removeItem(this.MEMBER_CACHE_KEY);
      console.log('🗑️ Cleared member cache');
    } catch (error) {
      console.warn('Failed to clear member cache:', error);
    }
  }

  // Get member name by ID (from cache or API)
  async getMemberName(memberId: string): Promise<string> {
    const cached = this.getCachedMembers();
    if (cached) {
      const member = cached.find(m => m.id === memberId);
      if (member) return member.fullName;
    }

    // If not in cache, try to get from API
    try {
      const members = await this.getMembers();
      const member = members.find(m => m.id === memberId);
      return member?.fullName || 'Unknown Member';
    } catch (error) {
      console.warn('Failed to get member name:', error);
      return 'Unknown Member';
    }
  }

  // Test connection
  async testConnection(): Promise<boolean> {
    try {
      await this.getUserInfo();
      return true;
    } catch (error) {
      console.error('Trello connection test failed:', error);
      return false;
    }
  }

  // Debug method to test member API endpoint directly
  async testMembersEndpoint(): Promise<any> {
    try {
      console.log('🧪 Testing get_members endpoint...');
      const response = await this.callMcp('get_members', {});
      console.log('🔍 Raw API response:', response);
      return response;
    } catch (error) {
      console.error('❌ Members endpoint test failed:', error);
      throw error;
    }
  }

  // Create a new label
  async createLabel(boardId: string, name: string, color: string): Promise<TrelloLabel> {
    const data = await this.callMcp('create_label', { boardId, name, color });
    return data.label || data;
  }

  // Ensure standard priority labels exist on a board
  async ensureStandardLabels(boardId: string): Promise<{ existing: TrelloLabel[], created: TrelloLabel[] }> {
    const standardLabels = [
      { name: "🔴 High Priority", color: "red" },
      { name: "🟡 Medium Priority", color: "yellow" },
      { name: "🟢 Low Priority", color: "green" }
    ];

    const existingLabels = await this.getBoardLabels(boardId);
    const existingLabelNames = existingLabels.map(label => label.name);
    
    const createdLabels: TrelloLabel[] = [];
    
    for (const standardLabel of standardLabels) {
      if (!existingLabelNames.includes(standardLabel.name)) {
        try {
          const newLabel = await this.createLabel(boardId, standardLabel.name, standardLabel.color);
          createdLabels.push(newLabel);
        } catch (error) {
          console.warn(`Failed to create standard label "${standardLabel.name}":`, error);
        }
      }
    }

    return {
      existing: existingLabels,
      created: createdLabels
    };
  }

  // Get all cards in a board
  async getBoardCards(boardId: string): Promise<TrelloCard[]> {
    const data = await this.callMcp('get_board_cards', { boardId });
    return data.cards || data;
  }

  // Get cards with specific label
  async getCardsByLabel(boardId: string, labelName: string): Promise<TrelloCard[]> {
    const data = await this.callMcp('get_cards_by_label', { boardId, labelName });
    return data.cards || data;
  }

  // Get card comments
  async getCardComments(cardId: string): Promise<any[]> {
    const data = await this.callMcp('get_card_comments', { cardId });
    return data.comments || data;
  }

  // Get card attachments
  async getCardAttachments(cardId: string): Promise<any[]> {
    const data = await this.callMcp('get_card_attachments', { cardId });
    return data.attachments || data;
  }

  // Get card members
  async getCardMembers(cardId: string): Promise<TrelloMember[]> {
    const data = await this.callMcp('get_card_members', { cardId });
    return data.members || data;
  }

  // Get card checklists
  async getCardChecklists(cardId: string): Promise<any[]> {
    const data = await this.callMcp('get_card_checklists', { cardId });
    return data.checklists || data;
  }

  // Get card activity (moves, updates, etc.)
  async getCardActivity(cardId: string): Promise<any[]> {
    const data = await this.callMcp('get_card_activity', { cardId });
    return data.activities || data;
  }
}

export const trelloService = new TrelloService(); 